import React, { useEffect, useRef, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

/*
 * 옵션 > 속성 > 값 순서 바꾸기용 공용 부품.
 *
 * 부모(상품·옵션·속성)마다 SortableList 를 따로 둔다. DndContext 가 부모마다 분리돼 있으므로
 * 속성을 다른 옵션으로, 값을 다른 속성으로 떨어뜨리는 일은 화면에서부터 일어날 수 없다.
 * 서버도 같은 규칙으로 한 번 더 막는다 (OptionUseCase.reorder*).
 */

/**
 * 드래그 결과를 반영한 새 순서. 움직이지 않았으면 null.
 * 화면에 일부만 보이는 목록(숨김·비활성 값 제외)도 전체 목록 안에서 옮기면 보이는 값끼리의 순서는 화면과 같다.
 */
export const moveById = (ids: number[], activeId: number, overId: number): number[] | null => {
  const from = ids.indexOf(activeId);
  const to = ids.indexOf(overId);
  if (from === -1 || to === -1 || from === to) return null;
  return arrayMove(ids, from, to);
};

/**
 * 놓는 즉시 저장하는 순서 상태.
 * 서버 응답 전에 화면 순서를 먼저 바꾸고, 저장에 실패하면 원래 순서로 되돌린다.
 */
export function useInstantReorder(
  serverIds: number[],
  save: (orderedIds: number[]) => Promise<void>,
) {
  const [ids, setIds] = useState(serverIds);
  const [error, setError] = useState("");
  const saving = useRef(false);
  const serverKey = serverIds.join(",");

  // 서버에서 새 목록을 받으면(추가·삭제·재조회) 그 순서를 따른다
  useEffect(() => {
    setIds(serverIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverKey]);

  const move = async (activeId: number, overId: number) => {
    if (saving.current) return;
    const next = moveById(ids, activeId, overId);
    if (!next) return;
    const previous = ids;
    setIds(next);
    setError("");
    saving.current = true;
    try {
      await save(next);
    } catch (e) {
      setIds(previous);
      setError(e instanceof Error && e.message ? e.message : "순서를 저장하지 못했습니다. 다시 시도해 주세요.");
    } finally {
      saving.current = false;
    }
  };

  return { ids, move, error, clearError: () => setError("") };
}

const toNumber = (id: string | number) => Number(String(id).split(":").pop());

interface SortableListProps {
  /** 이 목록 안에서만 쓰는 접두사. 부모가 다른 목록과 id 가 섞이지 않게 한다 */
  scope: string;
  ids: number[];
  onMove: (activeId: number, overId: number) => void;
  /** 세로 목록(옵션·속성) 또는 줄바꿈되는 카드/칩(값) */
  layout?: "vertical" | "grid";
  children: React.ReactNode;
}

export const SortableList: React.FC<SortableListProps> = ({ scope, ids, onMove, layout = "vertical", children }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onMove(toNumber(active.id), toNumber(over.id));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={ids.map((id) => `${scope}:${id}`)}
        strategy={layout === "grid" ? rectSortingStrategy : verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
};

interface SortableItemRenderProps {
  setNodeRef: (element: HTMLElement | null) => void;
  style: React.CSSProperties;
  handle: React.ReactNode;
  isDragging: boolean;
}

interface SortableItemProps {
  scope: string;
  id: number;
  /** 핸들 접근성 이름. 예: "'코팅' 옵션 순서 이동" */
  label: string;
  handleSize?: "md" | "sm";
  children: (props: SortableItemRenderProps) => React.ReactNode;
}

/** 목록의 한 항목. 핸들을 잡아야만 끌리므로 카드 클릭(펼치기·수정)과 겹치지 않는다 */
export const SortableItem: React.FC<SortableItemProps> = ({ scope, id, label, handleSize = "md", children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `${scope}:${id}` });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    position: "relative",
    zIndex: isDragging ? 20 : undefined,
  };
  const handle = (
    <button
      type="button"
      aria-label={label}
      title="끌어서 순서 바꾸기"
      onClick={(e) => e.stopPropagation()}
      className={`flex-shrink-0 flex items-center justify-center rounded text-[#BBBBBB] hover:text-[#4285F4] hover:bg-[#F5F8FF] cursor-grab active:cursor-grabbing touch-none ${
        handleSize === "sm" ? "w-5 h-5" : "w-6 h-6"
      }`}
      {...attributes}
      {...listeners}
    >
      <GripVertical className={handleSize === "sm" ? "w-3.5 h-3.5" : "w-4 h-4"} />
    </button>
  );
  return <>{children({ setNodeRef, style, handle, isDragging })}</>;
};

/** 순서 저장 실패 안내 */
export const ReorderError: React.FC<{ message: string; onClose: () => void }> = ({ message, onClose }) =>
  message ? (
    <div role="alert" className="mb-3 rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-4 py-2.5 flex items-center justify-between text-[13px] text-[#4285F4]">
      <span>{message}</span>
      <button type="button" onClick={onClose} className="ml-3 flex-shrink-0 font-medium hover:text-[#3367D6]">확인</button>
    </div>
  ) : null;
